import React, { useEffect, useState } from 'react';
import Navbar from '../../components/Navbar';
import api from '../../services/api';
import { uploadFile } from '../../services/upload';

const emptyForm = {
  title: '',
  imageUrl: '',
  linkUrl: '',
  placement: 'home',
  durationDays: 7,
  displayOrder: 0,
  status: 'active'
};

const SuperAdminBannerAds = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filters, setFilters] = useState({ status: '', placement: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [formData, setFormData] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [editingBanner, setEditingBanner] = useState(null);

  useEffect(() => {
    fetchBanners();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page]);

  const resetForm = () => {
    setFormData(emptyForm);
    setImageFile(null);
    setImagePreview('');
    setEditingBanner(null);
  };

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== ''))
      };
      const response = await api.get('/api/super/banner-ads', { params });
      setBanners(response.data.data || []);
      setPagination(response.data.pagination || pagination);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error fetching banner ads' });
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || '',
      imageUrl: banner.imageUrl || '',
      linkUrl: banner.linkUrl || '',
      placement: banner.placement || 'home',
      durationDays: banner.durationDays || 7,
      displayOrder: banner.displayOrder || 0,
      status: banner.status || 'active'
    });
    setImageFile(null);
    setImagePreview(banner.imageUrl || '');
    setMessage({ type: '', text: '' });
  };

  const submitBanner = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      let imageUrl = formData.imageUrl;
      if (imageFile) {
        const uploadResult = await uploadFile(imageFile, 'banner');
        if (!uploadResult.success) {
          setMessage({ type: 'error', text: uploadResult.message || 'Banner image upload failed' });
          setSaving(false);
          return;
        }
        imageUrl = uploadResult.url;
      }

      if (!imageUrl) {
        setMessage({ type: 'error', text: 'Banner image is required' });
        setSaving(false);
        return;
      }

      const payload = {
        ...formData,
        imageUrl,
        durationDays: Number(formData.durationDays || 7),
        displayOrder: Number(formData.displayOrder || 0)
      };

      if (editingBanner) {
        await api.put(`/api/super/banner-ads/${editingBanner._id}`, payload);
        setMessage({ type: 'success', text: 'Banner ad updated successfully' });
      } else {
        await api.post('/api/super/banner-ads', payload);
        setMessage({ type: 'success', text: 'Banner ad created successfully' });
      }

      resetForm();
      fetchBanners();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error saving banner ad' });
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (banner, status) => {
    try {
      await api.patch(`/api/super/banner-ads/${banner._id}/status`, { status });
      setMessage({ type: 'success', text: 'Banner status updated' });
      fetchBanners();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error updating banner status' });
    }
  };

  const deleteBanner = async (banner) => {
    const confirmed = window.confirm(`Delete banner ad${banner.title ? ` "${banner.title}"` : ''}?`);
    if (!confirmed) return;

    try {
      await api.delete(`/api/super/banner-ads/${banner._id}`);
      setMessage({ type: 'success', text: 'Banner ad deleted successfully' });
      fetchBanners();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error deleting banner ad' });
    }
  };

  return (
    <div className="app">
      <Navbar />
      <div className="container">
        <div className="page-header">
          <div>
            <h1 className="page-title">Banner Ads</h1>
            <p className="page-subtitle">Upload app banner images, attach links, and control how many days they appear.</p>
          </div>
        </div>

        {message.text && (
          <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`}>
            {message.text}
          </div>
        )}

        <div className="card">
          <h2 className="modal-title">{editingBanner ? 'Edit Banner Ad' : 'Create Banner Ad'}</h2>
          <form onSubmit={submitBanner}>
            <div className="filters">
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(event) => setFormData({ ...formData, title: event.target.value })}
                  placeholder="Optional admin title"
                />
              </div>
              <div className="form-group">
                <label>Placement</label>
                <input
                  type="text"
                  value={formData.placement}
                  onChange={(event) => setFormData({ ...formData, placement: event.target.value })}
                  placeholder="home"
                />
              </div>
              <div className="form-group">
                <label>Duration Days *</label>
                <input
                  type="number"
                  value={formData.durationDays}
                  onChange={(event) => setFormData({ ...formData, durationDays: event.target.value })}
                  min="1"
                  max="365"
                  required
                />
              </div>
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={formData.displayOrder}
                  onChange={(event) => setFormData({ ...formData, displayOrder: event.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(event) => setFormData({ ...formData, status: event.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Banner Link *</label>
              <input
                type="url"
                value={formData.linkUrl}
                onChange={(event) => setFormData({ ...formData, linkUrl: event.target.value })}
                placeholder="https://example.com"
                required
              />
            </div>

            <div className="form-group">
              <label>Banner Image *</label>
              <input type="file" accept="image/*" onChange={handleImageChange} />
              {imagePreview && (
                <div className="banner-preview">
                  <img src={imagePreview} alt="Banner preview" />
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : (editingBanner ? 'Update Banner' : 'Create Banner')}
              </button>
              {editingBanner && (
                <button type="button" className="btn btn-secondary" onClick={resetForm} disabled={saving}>
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="filters">
          <div className="form-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(event) => setFilters({ ...filters, status: event.target.value })}
            >
              <option value="">All active records</option>
              <option value="active">Active</option>
              <option value="scheduled">Scheduled</option>
              <option value="inactive">Inactive</option>
              <option value="expired">Expired</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>
          <div className="form-group">
            <label>Placement</label>
            <input
              type="text"
              value={filters.placement}
              onChange={(event) => setFilters({ ...filters, placement: event.target.value })}
              placeholder="home"
            />
          </div>
        </div>

        <div className="card">
          {loading ? (
            <div className="loading"><div className="spinner"></div></div>
          ) : (
            <>
              <table className="table">
                <thead>
                  <tr>
                    <th>Banner</th>
                    <th>Link</th>
                    <th>Placement</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Ends</th>
                    <th>Views</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {banners.map((banner) => (
                    <tr key={banner._id}>
                      <td>
                        <div className="banner-table-cell">
                          <img src={banner.imageUrl} alt={banner.title || 'Banner ad'} />
                          <span>{banner.title || 'Untitled'}</span>
                        </div>
                      </td>
                      <td>
                        <a href={banner.linkUrl} target="_blank" rel="noreferrer">
                          {banner.linkUrl}
                        </a>
                      </td>
                      <td>{banner.placement || 'home'}</td>
                      <td>{banner.status}</td>
                      <td>{banner.durationDays} days</td>
                      <td>{banner.endDate ? new Date(banner.endDate).toLocaleDateString() : 'N/A'}</td>
                      <td>{banner.viewCount || 0}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button className="btn btn-sm btn-secondary" type="button" onClick={() => handleEdit(banner)}>
                            Edit
                          </button>
                          <select
                            value={banner.status}
                            onChange={(event) => updateStatus(banner, event.target.value)}
                            className="btn btn-sm"
                          >
                            <option value="active">Active</option>
                            <option value="scheduled">Scheduled</option>
                            <option value="inactive">Inactive</option>
                            <option value="expired">Expired</option>
                            <option value="deleted">Deleted</option>
                          </select>
                          <button className="btn btn-sm btn-danger" type="button" onClick={() => deleteBanner(banner)}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {banners.length === 0 && (
                    <tr>
                      <td colSpan="8">No banner ads found.</td>
                    </tr>
                  )}
                </tbody>
              </table>

              {pagination.pages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                  <span>Page {pagination.page} of {pagination.pages}</span>
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                    disabled={pagination.page === pagination.pages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminBannerAds;
